import React, {useEffect, useRef, useState} from 'react';
import {TestRunJson} from "../../api/json/TestRunJson"
import ApiError from "../../api/ApiError"
import {AnyCustomModelJson} from "../../api/json/CustomModelJson"
import {PagedResponseJson} from "../../api/json/PagedResponseJson"
import ScrollPaginator from "../misc/ScrollPaginator"
import {TestRunRequests} from "../../api/requests/TestRunRequests"
import {Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from "@mui/material"
import TestRunTableSkeleton from "./TestRunTableSkeleton"
import TestRunRow from "./TestRunRow"
import {PredefinedOptionJson} from "../../api/json/PredefinedOptionJson"
import {ClassifierFilter, hasActiveClassifierFilter, matchesClassifierFilter, normalizeToClass} from "../../library/ClassifierFilter"
import TemplateUtil from "../../library/TemplateUtil"

interface TestRunsLoaderProps {
    model: AnyCustomModelJson
    classOptions?: PredefinedOptionJson[]
    classifierFilter?: ClassifierFilter
    refreshSignal?: number
    reloadQualityCheckStats: () => void
}

const headerCellSx = {textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, color: "text.secondary"} as const

function TestRunList({model, classOptions, classifierFilter, refreshSignal, reloadQualityCheckStats}: TestRunsLoaderProps) {
    const [pages, setPages] = useState<PagedResponseJson<TestRunJson>[]>([])
    const [error, setError] = useState<ApiError>()

    const testRuns = pages.flatMap(it => it.content)
    const classes = classOptions?.map(option => option.value) ?? []
    const filterActive = Boolean(classifierFilter && classes.length > 0 && hasActiveClassifierFilter(classifierFilter))

    useEffect(() => {
        setPages([])
    }, [])

    const requestedPages = useRef(new Set<number>())

    const loadPage = (pageNumber: number) => {
        if (requestedPages.current.has(pageNumber)) return
        requestedPages.current.add(pageNumber)
        TestRunRequests.list(model.qualityCheck.id, pageNumber, false)
            .then(response => setPages(current =>
                current.find(page => page.number === response.number) ? current : [...current, response]))
            .catch(error => {
                requestedPages.current.delete(pageNumber)
                setError(error)
            })
    }

    // A drilled-down list must cover every run, so pull the remaining pages without waiting on scroll.
    const lastPage = pages.length > 0 ? pages[pages.length - 1] : null
    useEffect(() => {
        if (!filterActive || !lastPage || lastPage.last) return
        loadPage(lastPage.number + 1)
    }, [filterActive, pages])

    // New runs stream in while the quality check executes: refresh loaded pages in place instead of
    // remounting the whole table, and let the page after the last be requested again.
    useEffect(() => {
        if (pages.length === 0) return
        pages.forEach(page => {
            TestRunRequests.list(model.qualityCheck.id, page.number, false)
                .then(response => setPages(current =>
                    current.map(existing => existing.number === response.number ? response : existing)))
                .catch(() => {})
        })
        requestedPages.current.delete((pages[pages.length - 1].number) + 1)
    }, [refreshSignal])

    const filteredTestRuns = filterActive
        ? testRuns.filter(testRun => {
            const predicted = normalizeToClass(testRun.output, classes)
            const expected = normalizeToClass(
                TemplateUtil.renderCompletionEquivalent(model.template, testRun.row.cells), classes)
            return matchesClassifierFilter(classifierFilter!, predicted, expected, testRun.confidence)
        })
        : testRuns

    const didUpdate = (updated: TestRunJson) => {
        setPages(current => current.map(page => ({
            ...page,
            content: page.content.map(existing => existing.row.id === updated.row.id ? {...updated} : existing),
        })))
    }

    const didDelete = (rowId: number) => {
        setPages(current => current.map(page => ({
            ...page,
            content: page.content.filter(existing => existing.row.id !== rowId),
        })))
        reloadQualityCheckStats()
    }

    return (
        <ScrollPaginator pages={pages} loadNextPage={loadPage} error={error} showInitialSpinner={false}>
            {filterActive &&
                <Typography variant="caption" color="text.secondary" sx={{display: "block", mb: 1}}>
                    Showing {filteredTestRuns.length} of {lastPage?.totalElements ?? testRuns.length} runs
                </Typography>
            }
            {pages.length === 0 && <TestRunTableSkeleton/>}
            {pages.length > 0 &&
            <TableContainer sx={{overflowX: "auto", bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, boxShadow: "none"}}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox"/>
                            <TableCell sx={{width: "45%"}}>
                                <Typography variant="caption" sx={headerCellSx}>Input</Typography>
                            </TableCell>
                            <TableCell><Typography variant="caption" sx={headerCellSx}>Predicted</Typography></TableCell>
                            <TableCell><Typography variant="caption" sx={headerCellSx}>Expected</Typography></TableCell>
                            <TableCell align="center"><Typography variant="caption" sx={headerCellSx}>Match</Typography></TableCell>
                            <TableCell padding="checkbox"/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredTestRuns.map(testRun =>
                            <TestRunRow key={testRun.row.id}
                                        qualityCheckJson={model.qualityCheck}
                                        template={model.template}
                                        testRun={testRun}
                                        classOptions={classOptions}
                                        didUpdate={didUpdate}
                                        didDelete={didDelete}/>,
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            }
        </ScrollPaginator>
    )
}

export default TestRunList;
